import React from "react";
import { Bot, CalendarDays, CreditCard, FileText, FolderOpen, History, LayoutDashboard, MessageSquare, ShieldAlert } from "lucide-react";
import { useAppContext } from "../../lib/mockState";

const SHARED_TABS = [
  { label: "Overview", icon: LayoutDashboard, route: null },
  { label: "Documents", icon: FolderOpen, route: "/documents" },
  { label: "Drafting", icon: FileText, route: "/drafting" },
  { label: "Intelligence", icon: Bot, route: null },
  { label: "Chronicle", icon: History, route: "/communications" },
  { label: "Calendar", icon: CalendarDays, route: "/calendar" },
  { label: "Billing", icon: CreditCard, route: "/billing" },
  { label: "Portal", icon: MessageSquare, route: "/communications" },
] as const;

interface MatterShellBannerProps {
  species: "CASE" | "CONTRACT";
}

export const MatterShellBanner: React.FC<MatterShellBannerProps> = ({ species }) => {
  const { activeMatterId, matters, alerts, setCurrentRoute } = useAppContext();
  const matter = matters.find((candidate) => candidate.matterId === activeMatterId);
  const unresolvedAlerts = alerts.filter((alert) => alert.matterId === activeMatterId && alert.status === "UNRESOLVED");

  return (
    <div className="bg-white border border-slate-200/70 rounded-xl shadow-xs p-3 space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
            <span>Clients</span>
            <span>/</span>
            <span>{matter?.clientName || "Client"}</span>
            <span>/</span>
            <span className="text-orange-600">{species === "CASE" ? "Case Matter" : "Contract Matter"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <h2 className="text-sm font-black text-slate-950 truncate">{matter?.title || activeMatterId}</h2>
            <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-[9px] font-mono font-bold">{activeMatterId}</span>
            <span className="px-2 py-0.5 rounded-lg bg-orange-50 text-orange-700 text-[9px] font-mono font-bold">Matter Mode</span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-600">
          <ShieldAlert size={13} className={unresolvedAlerts.length ? "text-rose-600" : "text-emerald-600"} />
          <span>{unresolvedAlerts.length} unresolved matter alerts</span>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto rounded-xl bg-slate-100/70 p-1">
        {SHARED_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = tab.route === null;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => tab.route && setCurrentRoute(tab.route)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[10px] font-bold transition-colors ${
                active ? "bg-slate-900 text-white shadow-3xs" : "text-slate-600 hover:bg-white hover:text-slate-950"
              }`}
            >
              <Icon size={12} className={active ? "text-orange-300" : "text-slate-400"} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
